import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Package, MapPin, User as UserIcon, Heart, LogOut, Clock } from "lucide-react";
import { useStore } from "../context/StoreContext";
import api, { img, money } from "../lib/api";
import { toast } from "sonner";

export default function Account() {
  const { user, authReady, logout } = useStore();
  const nav = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [wl, setWl] = useState([]);
  const [profile, setProfile] = useState({ name: "", phone: "" });
  const [newAddr, setNewAddr] = useState({ address: "", city: "", state: "", pincode: "", country: "India" });

  useEffect(() => { if (authReady && !user) nav("/login"); }, [authReady, user]);
  useEffect(() => {
    if (!user) return;
    setProfile({ name: user.name, phone: user.phone || "" });
    api.get("/orders").then(r => setOrders(r.data));
    api.get("/addresses").then(r => setAddresses(r.data));
    api.get("/wishlist").then(r => setWl(r.data));
  }, [user]);

  if (!user) return null;

  const tabs = [["orders", "My Orders", Package], ["addresses", "Addresses", MapPin], ["wishlist", "Wishlist", Heart], ["profile", "Profile", UserIcon]];
  const paidCount = orders.filter(o => o.payment.status === "paid").length;
  const spent = orders.filter(o => o.payment.status === "paid").reduce((a, o) => a + o.total, 0);

  const saveProfile = async () => { await api.put("/auth/profile", profile); toast.success("Profile updated"); };
  const addAddress = async () => { const r = await api.post("/addresses", newAddr); setAddresses(r.data); setNewAddr({ address: "", city: "", state: "", pincode: "", country: "India" }); toast.success("Address saved"); };
  const delAddress = async (id) => { const r = await api.delete(`/addresses/${id}`); setAddresses(r.data); };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
      <div className="flex items-end justify-between mb-10">
        <div><span className="eyebrow text-moss">My Account</span><h1 className="serif text-5xl mt-2">Hello, {user.name.split(" ")[0]}</h1></div>
        <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-forest/60 hover:text-forest" data-testid="account-logout"><LogOut size={15} /> Logout</button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-12">
        {[["Orders", orders.length], ["Paid Orders", paidCount], ["Total Spent", money(spent)]].map(([l, v]) => (
          <div key={l} className="bg-beige/40 p-6"><div className="eyebrow text-forest/50">{l}</div><div className="serif text-3xl mt-1">{v}</div></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-10">
        <aside className="flex lg:flex-col gap-1 overflow-x-auto hide-scrollbar">
          {tabs.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`account-tab-${id}`}
              className={`flex items-center gap-3 px-4 py-3 text-sm whitespace-nowrap transition-colors ${tab === id ? "bg-forest text-ivory" : "text-forest/65 hover:bg-beige/50"}`}>
              <Icon size={16} /> {label}
            </button>
          ))}
        </aside>

        <div>
          {tab === "orders" && (
            <div className="space-y-4">
              {orders.length === 0 && <p className="text-forest/50 py-10">No orders yet. <Link to="/shop" className="underline">Start shopping</Link>.</p>}
              {orders.map((o) => (
                <div key={o.id} className="border border-sage/30 p-6" data-testid={`account-order-${o.order_number}`}>
                  <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div><div className="serif text-xl">{o.order_number}</div><div className="text-xs text-forest/50">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div></div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs uppercase tracking-wide px-3 py-1 ${o.payment.status === "paid" ? "bg-moss/20 text-moss" : "bg-brown/15 text-brown"}`}>{o.payment.status}</span>
                      <span className="text-xs uppercase tracking-wide px-3 py-1 bg-sage/20 text-forest capitalize">{o.fulfillment_status.replace(/_/g, " ")}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-4">
                    {o.items.slice(0, 4).map((it) => <img key={it.slug} src={img(it.image)} alt="" className="h-14 w-12 object-cover bg-beige/40" />)}
                    <span className="text-sm text-forest/60">{o.items.length} item(s)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="serif text-xl">{money(o.total)}</span>
                    <Link to={`/track?order=${o.order_number}`} className="text-xs uppercase tracking-[0.14em] underline text-forest/70 hover:text-forest">Track Order</Link>
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === "addresses" && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                {addresses.map((ad) => (
                  <div key={ad.id} className="border border-sage/30 p-5 text-sm">
                    <p>{ad.address}</p><p>{ad.city}, {ad.state} {ad.pincode}</p><p>{ad.country}</p>
                    <button onClick={() => delAddress(ad.id)} className="text-xs text-brown mt-3 underline" data-testid={`del-addr-${ad.id}`}>Remove</button>
                  </div>
                ))}
              </div>
              <div className="bg-beige/40 p-6 max-w-lg">
                <h3 className="serif text-xl mb-4">Add Address</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <input className="input-line sm:col-span-2" placeholder="Address" value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} data-testid="addr-address" />
                  <input className="input-line" placeholder="City" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} data-testid="addr-city" />
                  <input className="input-line" placeholder="State" value={newAddr.state} onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })} data-testid="addr-state" />
                  <input className="input-line" placeholder="PIN" value={newAddr.pincode} onChange={(e) => setNewAddr({ ...newAddr, pincode: e.target.value })} data-testid="addr-pin" />
                </div>
                <button onClick={addAddress} className="btn-primary mt-5" data-testid="addr-save">Save Address</button>
              </div>
            </div>
          )}

          {tab === "wishlist" && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
              {wl.length === 0 && <p className="text-forest/50">Save what speaks to you.</p>}
              {wl.map((p) => (
                <Link key={p.slug} to={`/product/${p.slug}`} className="group">
                  <div className="overflow-hidden aspect-[4/5] bg-beige/40 mb-3"><img src={img((p.images || [])[1] || p.images?.[0])} alt="" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                  <div className="serif text-lg">{p.name}</div><div className="text-sm">{money(p.price)}</div>
                </Link>
              ))}
            </div>
          )}

          {tab === "profile" && (
            <div className="max-w-md space-y-5">
              <div><label className="text-xs uppercase tracking-wide text-forest/55">Name</label><input className="input-line mt-1" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" /></div>
              <div><label className="text-xs uppercase tracking-wide text-forest/55">Email</label><input className="input-line mt-1 opacity-60" value={user.email} disabled /></div>
              <div><label className="text-xs uppercase tracking-wide text-forest/55">Phone</label><input className="input-line mt-1" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="profile-phone" /></div>
              <button onClick={saveProfile} className="btn-primary" data-testid="profile-save">Save Changes</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
